import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';

interface Highlight {
  category: string;
  icon: string;
  text: string;
}

interface MonthlyHighlightsData {
  month: string;
  highlights: Highlight[];
}

const getMonthName = (monthNumber: number): string => {
  const months = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
  ];
  return months[monthNumber - 1];
};

const getMonthlyHighlights = (month: number): Highlight[] => {
  const highlights = {
    3: [
      { category: 'בית', icon: '🧼', text: 'להתחיל לנקות לפסח בהדרגה' },
      { category: 'נסיעות', icon: '✈️', text: 'טיולים בצפון – הפריחה בשיאה' },
      { category: 'פיננסים', icon: '💰', text: 'לבדוק כפל ביטוחים לפני החג' }
    ],
    4: [
      { category: 'בית', icon: '🏠', text: 'לבצע בדק בית לקראת הקיץ' },
      { category: 'גינה', icon: '🌱', text: 'לשתול צמחי תבלין לקיץ' },
      { category: 'פיננסים', icon: '💳', text: 'לבדוק זכאות להחזרי מס' }
    ],
    5: [
      { category: 'נסיעות', icon: '✈️', text: 'להזמין טיסות לחופשת הקיץ' },
      { category: 'בית', icon: '❄️', text: 'לבדוק את מערכת המיזוג' },
      { category: 'קניות', icon: '🛍️', text: 'מבצעי סוף עונה על ביגוד' }
    ],
    6: [
      { category: 'חינוך', icon: '📚', text: 'לרשום לקייטנות ופעילויות קיץ' },
      { category: 'בית', icon: '🏊‍♂️', text: 'להכין את הבית לקיץ' },
      { category: 'בריאות', icon: '🧴', text: 'לחדש מלאי קרם הגנה' }
    ],
    7: [
      { category: 'חינוך', icon: '🎒', text: 'לקנות ציוד לשנת הלימודים' },
      { category: 'בית', icon: '🌡️', text: 'לוודא שהמזגן תקין' },
      { category: 'פיננסים', icon: '💰', text: 'לתכנן תקציב לחגים' }
    ],
    8: [
      { category: 'חינוך', icon: '📚', text: 'להשלים קניות לבית הספר' },
      { category: 'בית', icon: '🧹', text: 'ניקיון יסודי לקראת החגים' },
      { category: 'קניות', icon: '🛒', text: 'לקנות מתנות לראש השנה' }
    ],
    9: [
      { category: 'בית', icon: '🏠', text: 'להכין את הבית לחורף' },
      { category: 'גינה', icon: '🌱', text: 'לשתול צמחי חורף' },
      { category: 'פיננסים', icon: '💳', text: 'לבדוק הוצאות החגים' }
    ],
    10: [
      { category: 'בית', icon: '🔧', text: 'לבדוק דוד שמש ומערכת חימום' },
      { category: 'בריאות', icon: '💉', text: 'להתחסן נגד שפעת' },
      { category: 'קניות', icon: '🧥', text: 'לקנות ביגוד חורף במבצעים' }
    ],
    11: [
      { category: 'בית', icon: '🏠', text: 'לאטום חלונות ודלתות' },
      { category: 'פיננסים', icon: '💰', text: 'לנצל תקציב קרן השתלמות' },
      { category: 'קניות', icon: '🛍️', text: 'לנצל מבצעי Black Friday' }
    ],
    12: [
      { category: 'בית', icon: '🔧', text: 'לתחזק מערכות חימום' },
      { category: 'פיננסים', icon: '📊', text: 'לסכם שנה כלכלית' },
      { category: 'קניות', icon: '🎁', text: 'לנצל מבצעי סוף שנה' }
    ],
    1: [
      { category: 'בית', icon: '🏠', text: 'לעשות סדר בארונות' },
      { category: 'פיננסים', icon: '📋', text: 'לתכנן תקציב שנתי' },
      { category: 'בריאות', icon: '🏃‍♂️', text: 'להירשם לחדר כושר' }
    ],
    2: [
      { category: 'בית', icon: '🧹', text: 'ניקיון אביב מוקדם' },
      { category: 'גינה', icon: '🌱', text: 'להכין את הגינה לאביב' },
      { category: 'פיננסים', icon: '📑', text: 'להכין מסמכים למס הכנסה' }
    ]
  };

  return highlights[month] || [];
};

export const MonthlyHighlights: React.FC = () => {
  const [data, setData] = useState<MonthlyHighlightsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const monthName = getMonthName(currentMonth);
      const highlights = getMonthlyHighlights(currentMonth);

      if (highlights.length === 0) {
        setError('אין המלצות זמינות לחודש הנוכחי');
        return;
      }

      setData({
        month: monthName,
        highlights: highlights
      });
    } catch (error) {
      console.error('Error setting monthly highlights:', error);
      setError('אירעה שגיאה בטעינת ההמלצות החודשיות');
    }
  }, []);

  if (error) {
    return (
      <div className="w-full md:col-span-2 bg-red-50 rounded-lg p-4 shadow-sm mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.highlights.length) return null;

  return (
    <div className="w-full md:col-span-2 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300 mb-6">
      <div className="flex items-start gap-4">
        <Calendar className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            מה כדאי לעשות ב{data.month}?
          </h2>
          
          <div className="space-y-4">
            {data.highlights.map((highlight, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="text-2xl" role="img" aria-label={highlight.category}>
                  {highlight.icon}
                </span>
                <div>
                  <span className="font-medium text-gray-700">{highlight.category}:</span>
                  <span className="text-gray-600 mr-2">{highlight.text}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-sm text-gray-500 mt-4">
            מתעדכן אוטומטית בכל חודש
          </p>
        </div>
      </div>
    </div>
  );
};